import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import {
  CartProductInput,
  useCart,
} from "../contexts/CartContext";
import { productApi } from "../api/productApi";
import { getImageUrl } from "../utils/getImageUrl";

import serviceContactImage from "../assets/product/service_contact.png";
import serviceInspectImage from "../assets/product/service_inspect.png";
import serviceCareImage from "../assets/product/service_care.png";
import serviceHandoverImage from "../assets/product/service_handover.png";

import "../styles/ProductDetailPage.css";

type ProductImage = {
  url: string;
  fileName?: string;
  alt: string;
  isMain: boolean;
  sortOrder: number;
};

type ProductSpecification = {
  label: string;
  value: string;
};

type Product = {
  id: string;
  name: string;
  slug: string;
  sku: string;
  parentCategoryId?: string;
  parentCategoryName?: string;
  categoryId?: string;
  categoryName: string;
  price: number;
  salePrice: number;
  shortDescription: string;
  description: string;
  images: ProductImage[];
  specifications: ProductSpecification[];
  tags: string[];
  seo?: {
    title?: string;
    description?: string;
    keywords?: string[];
    canonicalUrl?: string;
    ogImage?: string;
  };
  visibility: {
    isPublished: boolean;
    showOnMenu: boolean;
  };
  status: "active" | "draft" | "inactive";
};

const ZALO_URL = "https://zalo.me/0901191141";

const purchaseCommitments = [
  {
    icon: "shield",
    title: "Ngọc tuyển chọn",
    text: "Kiểm tra màu, vân, độ bóng và cảm giác tổng thể trước khi tư vấn.",
  },
  {
    icon: "image",
    title: "Hình ảnh thật",
    text: "Gửi thêm ảnh hoặc video thực tế nếu khách cần xem kỹ từng vùng màu.",
  },
  {
    icon: "size",
    title: "Tư vấn size",
    text: "Dựa trên chu vi cổ tay, dáng bàn tay và thói quen đeo.",
  },
  {
    icon: "care",
    title: "Chăm sóc sau mua",
    text: "Hướng dẫn bảo quản để giữ độ bóng và vẻ trong của ngọc.",
  },
];

const afterSaleServices = [
  {
    icon: "shield",
    title: "Bảo hành chính hãng",
    text: "Hỗ trợ theo chính sách áp dụng và xác nhận rõ tình trạng sản phẩm trước khi xử lý.",
  },
  {
    icon: "refresh",
    title: "Vệ sinh & làm mới",
    text: "Làm sạch, chăm sóc và hướng dẫn bảo quản để giữ độ bóng và độ trong của ngọc.",
  },
  {
    icon: "inspect",
    title: "Kiểm tra cẩn thận",
    text: "Đánh giá màu, vân và tổng thể sản phẩm trước khi đề xuất phương án phù hợp cho khách.",
  },
  {
    icon: "message",
    title: "Tư vấn hậu mãi",
    text: "Hỗ trợ qua Zalo khi khách cần kiểm tra thêm, hỏi cách đeo, bảo quản hay chăm sóc định kỳ.",
  },
];

const afterSaleSteps = [
  {
    number: "1",
    title: "Liên hệ",
    text: "Khách liên hệ qua Zalo hoặc hotline để mô tả nhu cầu, tình trạng sản phẩm và mong muốn hỗ trợ.",
    image: serviceContactImage,
    alt: "Liên hệ tư vấn hậu mãi",
  },
  {
    number: "2",
    title: "Tiếp nhận & kiểm tra",
    text: "Chuyên viên kiểm tra màu, vân, độ bóng, kích thước và xác nhận lại tình trạng thực tế trước khi xử lý.",
    image: serviceInspectImage,
    alt: "Tiếp nhận và kiểm tra vòng ngọc",
  },
  {
    number: "3",
    title: "Xử lý & chăm sóc",
    text: "Tiến hành vệ sinh, làm mới hoặc chăm sóc theo nhu cầu với thao tác cẩn trọng và môi trường phù hợp.",
    image: serviceCareImage,
    alt: "Xử lý và chăm sóc vòng ngọc",
  },
  {
    number: "4",
    title: "Bàn giao",
    text: "Sản phẩm được bàn giao lại cùng hướng dẫn bảo quản, thông tin xác nhận và hỗ trợ hậu mãi khi cần.",
    image: serviceHandoverImage,
    alt: "Bàn giao sản phẩm cho khách",
  },
];

const getImageSource = (image?: ProductImage | null) =>
  image?.url ? getImageUrl(image.url) : "";

const getMainProductImage = (product?: Product | null) =>
  product?.images?.find((image) => image.isMain) ||
  product?.images?.[0] ||
  null;

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 12h14" />
    <path d="m14 7 5 5-5 5" />
  </svg>
);

const HeartIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M20.5 8.8c0 5-8.5 10.1-8.5 10.1S3.5 13.8 3.5 8.8A4.3 4.3 0 0 1 12 7.6a4.3 4.3 0 0 1 8.5 1.2Z" />
  </svg>
);

const ShareIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="18" cy="5" r="2.2" />
    <circle cx="6" cy="12" r="2.2" />
    <circle cx="18" cy="19" r="2.2" />
    <path d="m8 11 8-5M8 13l8 5" />
  </svg>
);

const MessageIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 5.5h16v11H9l-5 3v-14Z" />
    <path d="M8 9h8M8 13h5" />
  </svg>
);

const RulerIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="m4 17 13-13 3 3-13 13H4v-3Z" />
    <path d="m14 7 3 3M11 10l2 2M8 13l3 3" />
  </svg>
);

const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 3 5.5 5.6v5.8c0 4.1 2.7 7.7 6.5 9.6 3.8-1.9 6.5-5.5 6.5-9.6V5.6L12 3Z" />
    <path d="m9.3 12 1.8 1.8 3.9-4" />
  </svg>
);

const RefreshCareIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M3 12h3l2-5 3.2 10 2.2-6H21" />
  </svg>
);

const InspectIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="10" cy="10" r="5.5" />
    <path d="m14.2 14.2 5 5" />
    <path d="M10 7v6M7 10h6" />
  </svg>
);

const ImageIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <rect x="4" y="5" width="16" height="14" rx="2" />
    <circle cx="9" cy="10" r="1.5" />
    <path d="m6.5 17 4.2-4.2 2.8 2.8 2-2 2.5 3.4" />
  </svg>
);

const CareIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 4v5M9.5 6.5 12 9l2.5-2.5" />
    <path d="M5 12.5c2.1 0 3.3 1.1 4.2 2.1.8.9 1.5 1.7 2.8 1.7 1.4 0 2.2-.9 3-1.8.9-1 1.9-2 4-2" />
    <path d="M5 17.5c2.1 0 3.3 1.1 4.2 2.1" />
  </svg>
);

const PurchaseIcon = ({ type }: { type: string }) => {
  if (type === "image") return <ImageIcon />;
  if (type === "size") return <RulerIcon />;
  if (type === "care") return <CareIcon />;
  return <ShieldIcon />;
};

const AfterSaleServiceIcon = ({ type }: { type: string }) => {
  if (type === "refresh") return <RefreshCareIcon />;
  if (type === "inspect") return <InspectIcon />;
  if (type === "message") return <MessageIcon />;
  return <ShieldIcon />;
};

const ProductDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const { addToCart } = useCart();

  const navigationProduct = useMemo(() => {
    const state = location.state as
      | { productPreview?: Product }
      | null;

    const preview = state?.productPreview;
    return preview && preview.slug === slug ? preview : null;
  }, [location.state, slug]);

  const [product, setProduct] = useState<Product | null>(
    () => navigationProduct
  );
  const [selectedImage, setSelectedImage] = useState<ProductImage | null>(
    () => getMainProductImage(navigationProduct)
  );
  const [loading, setLoading] = useState(() => !navigationProduct);
  const [message, setMessage] = useState("");
  const [addedMessage, setAddedMessage] = useState("");
  const [isSizeGuideOpen, setIsSizeGuideOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    const previousTitle = document.title;

    if (navigationProduct) {
      setProduct(navigationProduct);
      setSelectedImage(getMainProductImage(navigationProduct));
      setLoading(false);
      setMessage("");
      document.title =
        navigationProduct.seo?.title ||
        `${navigationProduct.name} | King Jades`;
    }

    const fetchProductDetail = async () => {
      if (!slug) {
        if (mounted) {
          setMessage("Không tìm thấy đường dẫn sản phẩm.");
          setLoading(false);
        }
        return;
      }

      try {
        if (!navigationProduct) setLoading(true);

        setMessage("");
        setAddedMessage("");

        const data = await productApi.getPublicProductDetail(slug);
        const productData = data?.product as Product | null | undefined;

        if (!productData) {
          throw new Error("API không trả về dữ liệu sản phẩm.");
        }

        if (!mounted) return;

        setProduct(productData);
        setSelectedImage(getMainProductImage(productData));

        document.title =
          productData.seo?.title ||
          `${productData.name} | King Jades`;
      } catch (requestError) {
        if (!mounted) return;

        const error = requestError as Error;
        setMessage(
          error.message || "Không thể tải chi tiết sản phẩm."
        );
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchProductDetail();

    return () => {
      mounted = false;
      document.title = previousTitle;
    };
  }, [navigationProduct, slug]);

  useEffect(() => {
    if (!isSizeGuideOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsSizeGuideOpen(false);
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isSizeGuideOpen]);

  const sortedImages = useMemo(() => {
    if (!product?.images?.length) return [];

    return [...product.images].sort((first, second) => {
      if (first.isMain && !second.isMain) return -1;
      if (!first.isMain && second.isMain) return 1;

      return (first.sortOrder || 0) - (second.sortOrder || 0);
    });
  }, [product]);

  const contactUrl = useMemo(() => {
    if (!product) return "/contact";

    const params = new URLSearchParams({
      productId: product.id,
      productName: product.name,
      productSlug: product.slug,
    });

    return `/contact?${params.toString()}`;
  }, [product]);

  if (loading && !product) {
    return (
      <>
        <Header />
        <div className="kj-product-detail-page">
          <main className="kj-product-detail-main">
            <section className="kj-pdp-state">
              <div className="kj-pdp-spinner" />
              <span className="kj-pdp-eyebrow">King Jades Collection</span>
              <h1>Đang tải chi tiết sản phẩm...</h1>
            </section>
          </main>
        </div>
        <Footer />
      </>
    );
  }

  if (!product) {
    return (
      <>
        <Header />
        <div className="kj-product-detail-page">
          <main className="kj-product-detail-main">
            <section className="kj-pdp-state kj-pdp-state--error">
              <span className="kj-pdp-eyebrow">Product Not Found</span>
              <h1>Không thể tải sản phẩm</h1>
              <p>
                {message ||
                  "Dữ liệu sản phẩm không tồn tại hoặc API không trả về sản phẩm."}
              </p>
              <Link to="/products">Quay lại bộ sưu tập</Link>
            </section>
          </main>
        </div>
        <Footer />
      </>
    );
  }

  const findSpecification = (keywords: string[]) => {
    if (!product.specifications?.length) return "";

    const specification = product.specifications.find((item) => {
      const label = item.label.toLowerCase();
      return keywords.some((keyword) =>
        label.includes(keyword.toLowerCase())
      );
    });

    return specification?.value || "";
  };

  const materialText =
    findSpecification(["chất liệu", "material", "loại ngọc", "jade"]) ||
    product.categoryName ||
    "Đang cập nhật";

  const colorText =
    findSpecification(["màu", "color", "sắc"]) || "Đang cập nhật";

  const sizeText =
    findSpecification(["size", "kích thước", "đường kính", "chu vi", "lọt lòng"]) ||
    "Liên hệ tư vấn size";

  const shapeText =
    findSpecification(["dáng", "shape", "kiểu vòng"]) || "Round bangle";

  const widthText =
    findSpecification(["bản vòng", "bề rộng", "width"]) || "Đang cập nhật";

  const clarityText =
    findSpecification(["độ trong", "clarity", "trong"]) || "Đang cập nhật";

  const jadeTypeText =
    findSpecification(["loại ngọc", "type", "jadeite"]) ||
    product.parentCategoryName ||
    product.categoryName ||
    "Đang cập nhật";

  const sizeOptions = (() => {
    const matches = sizeText.match(/\d+(?:[.,]\d+)?/g) || [];
    const normalized = matches
      .map((item) => item.replace(",", "."))
      .slice(0, 4);

    return normalized.length >= 3
      ? normalized
      : ["54", "56.5", "58", "60"];
  })();

  const detailRows = [
    ["Loại sản phẩm", product.categoryName || "Vòng ngọc bangle"],
    ["Chất liệu", materialText],
    ["Loại ngọc", jadeTypeText],
    ["Màu sắc", colorText],
    ["Dáng vòng", shapeText],
    ["Bản vòng", widthText],
    ["Lọt lòng", sizeText],
    ["Độ trong", clarityText],
  ];

  const buildCartProduct = (
    productData: Product
  ): CartProductInput => {
    const image =
      selectedImage ||
      productData.images?.find((item) => item.isMain) ||
      productData.images?.[0] ||
      null;

    return {
      id: productData.id,
      name: productData.name,
      slug: productData.slug,
      sku: productData.sku,
      categoryName: productData.categoryName,
      price: productData.price,
      salePrice: productData.salePrice,
      image: getImageSource(image),
      shortDescription: productData.shortDescription,
    };
  };

  const handleAddToCart = () => {
    addToCart(buildCartProduct(product), 1);
    setAddedMessage("Đã thêm sản phẩm vào giỏ quan tâm.");

    window.setTimeout(() => setAddedMessage(""), 2400);
  };

  return (
    <>
      <Header />

      <div className="kj-product-detail-page">
        <main className="kj-product-detail-main">
          <section className="kj-pdp-hero">
            <div className="kj-pdp-hero__glow kj-pdp-hero__glow--left" />
            <div className="kj-pdp-hero__glow kj-pdp-hero__glow--right" />

            <div className="kj-pdp-shell kj-pdp-hero__layout">
              <section className="kj-pdp-media-card" aria-label="Hình ảnh sản phẩm">
                <div className="kj-pdp-media-card__topbar">
                  <strong>09:47</strong>

                  <span>KINGJADES</span>

                  <div>
                    <button
                      type="button"
                      onClick={handleAddToCart}
                      aria-label="Thêm vào giỏ quan tâm"
                      title="Thêm vào giỏ quan tâm"
                    >
                      <HeartIcon />
                    </button>

                    <button
                      type="button"
                      aria-label="Chia sẻ sản phẩm"
                      title="Chia sẻ sản phẩm"
                      onClick={() => {
                        if (navigator.share) {
                          navigator
                            .share({
                              title: product.name,
                              text: product.shortDescription,
                              url: window.location.href,
                            })
                            .catch(() => undefined);
                        }
                      }}
                    >
                      <ShareIcon />
                    </button>
                  </div>
                </div>

                <div className="kj-pdp-media-card__visual">
                  {getImageSource(selectedImage) ? (
                    <img
                      src={getImageSource(selectedImage)}
                      alt={selectedImage?.alt || product.name}
                    />
                  ) : (
                    <div className="kj-pdp-image-placeholder">
                      <span>KJ</span>
                      <small>King Jades</small>
                    </div>
                  )}

                  <span className="kj-pdp-media-card__video-label">
                    Firefly Video
                  </span>
                </div>

                <div className="kj-pdp-media-card__product-row">
                  <span className="kj-pdp-media-card__jade-dot" />
                  <strong>{product.name}</strong>

                  <button
                    type="button"
                    onClick={handleAddToCart}
                    aria-label="Thêm vào giỏ quan tâm"
                  >
                    <HeartIcon />
                  </button>
                </div>

                <div className="kj-pdp-media-card__thumbs">
                  {(sortedImages.length > 0
                    ? sortedImages.slice(0, 4)
                    : [null, null, null, null]
                  ).map((image, index) => (
                    <button
                      type="button"
                      key={image?.url || `placeholder-${index}`}
                      className={
                        image &&
                        selectedImage?.url === image.url
                          ? "active"
                          : ""
                      }
                      onClick={() => {
                        if (image) setSelectedImage(image);
                      }}
                      aria-label={`Xem ảnh ${index + 1}`}
                    >
                      {image ? (
                        <img
                          src={getImageSource(image)}
                          alt={image.alt || product.name}
                        />
                      ) : (
                        <span />
                      )}
                    </button>
                  ))}
                </div>

                <div className="kj-pdp-media-card__actions">
                  <a href={ZALO_URL} target="_blank" rel="noreferrer">
                    Tư vấn Zalo
                  </a>

                  <button
                    type="button"
                    onClick={() => setIsSizeGuideOpen(true)}
                  >
                    Chọn size
                  </button>
                </div>

                {addedMessage && (
                  <div className="kj-pdp-media-card__toast" role="status">
                    {addedMessage}
                  </div>
                )}
              </section>

              <article className="kj-pdp-hero-copy">
                <span className="kj-pdp-eyebrow">
                  Thông tin sản phẩm
                </span>

                <h1>{product.name}</h1>

                <p className="kj-pdp-hero-copy__description">
                  {product.shortDescription ||
                    "Thiết kế tuyển chọn từ King Jades, được đối chiếu hình ảnh thực tế và tình trạng sản phẩm trước khi tư vấn."}
                </p>

                <div className="kj-pdp-tags">
                  {(product.tags?.length
                    ? product.tags.slice(0, 3)
                    : ["Type A Jadeite", "Multicolor", "Hand Selected"]
                  ).map((tag) => (
                    <span key={tag}>{tag}</span>
                  ))}
                </div>

                <section className="kj-pdp-consult-card">
                  <h2>Liên hệ tư vấn giá</h2>
                  <p>
                    KingJades xác nhận size, ảnh/video thực tế và tình trạng
                    sản phẩm trước khi chốt đơn.
                  </p>

                  <div className="kj-pdp-size-chips">
                    {sizeOptions.map((size) => (
                      <button
                        type="button"
                        key={size}
                        className={size === sizeOptions[1] ? "active" : ""}
                        onClick={() => setIsSizeGuideOpen(true)}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </section>

                <div className="kj-pdp-hero-copy__actions">
                  <a href={ZALO_URL} target="_blank" rel="noreferrer">
                    Tư vấn qua Zalo
                  </a>

                  <button
                    type="button"
                    onClick={() => setIsSizeGuideOpen(true)}
                  >
                    Hướng dẫn size
                  </button>
                </div>
              </article>
            </div>
          </section>

          <section className="kj-pdp-overview">
            <div className="kj-pdp-shell kj-pdp-overview__card">
              <section className="kj-pdp-spec-panel">
                <span className="kj-pdp-eyebrow">Thông tin sản phẩm</span>
                <h2>Chi tiết vòng</h2>

                <dl>
                  {detailRows.map(([label, value]) => (
                    <div key={label}>
                      <dt>{label}</dt>
                      <dd>{value}</dd>
                    </div>
                  ))}
                </dl>
              </section>

              <section className="kj-pdp-commitment-panel">
                <span className="kj-pdp-eyebrow">Cam kết KingJades</span>
                <h2>Tư vấn kỹ trước khi mua</h2>

                <div className="kj-pdp-commitment-grid">
                  {purchaseCommitments.map((item) => (
                    <article key={item.title}>
                      <span className="kj-pdp-commitment-icon">
                        <PurchaseIcon type={item.icon} />
                      </span>
                      <h3>{item.title}</h3>
                      <p>{item.text}</p>
                    </article>
                  ))}
                </div>
              </section>
            </div>
          </section>

          <section className="kj-pdp-size-card-section">
            <div className="kj-pdp-shell kj-pdp-size-card">
              <div className="kj-pdp-size-card__copy">
                <span className="kj-pdp-eyebrow">Tư vấn size vòng</span>
                <h2>Bạn chưa chắc size vòng?</h2>
                <p>
                  Vòng ngọc là vòng cứng, nên size không chỉ dựa vào cổ tay
                  mà còn phụ thuộc vào phần rộng nhất của bàn tay. KingJades
                  có thể tư vấn lại qua Zalo trước khi xác nhận đơn hàng.
                </p>

                <a href={ZALO_URL} target="_blank" rel="noreferrer">
                  Gửi ảnh qua Zalo
                </a>
              </div>

              <div className="kj-pdp-size-card__steps">
                <article>
                  <span>1</span>
                  <div>
                    <h3>Đo phần rộng nhất của bàn tay</h3>
                    <p>Khép các ngón tay lại, đo quanh phần rộng nhất của bàn tay.</p>
                  </div>
                </article>

                <article>
                  <span>2</span>
                  <div>
                    <h3>Đo chu vi cổ tay</h3>
                    <p>Dùng thước dây mềm đo sát phần xương cổ tay.</p>
                  </div>
                </article>

                <article>
                  <span>3</span>
                  <div>
                    <h3>Đo vòng đang đeo vừa</h3>
                    <p>Nếu đã có vòng vừa tay, đo đường kính lọt lòng bên trong.</p>
                  </div>
                </article>
              </div>
            </div>
          </section>

          <section className="kj-pdp-after-sale">
            <div className="kj-pdp-shell">
              <header className="kj-pdp-after-sale__heading">
                <div>
                  <span className="kj-pdp-eyebrow">After Sales Service</span>
                  <h2>Chăm sóc sau bán hàng</h2>
                </div>

                <p>
                  KingJades đồng hành cùng khách hàng sau khi mua — từ tư vấn,
                  kiểm tra, vệ sinh đến bàn giao lại sản phẩm trong tinh thần
                  chỉn chu và an tâm.
                </p>
              </header>

              <div className="kj-pdp-after-sale__services">
                {afterSaleServices.map((service) => (
                  <article key={service.title}>
                    <div className="kj-pdp-after-sale__service-icon">
                      <AfterSaleServiceIcon type={service.icon} />
                    </div>
                    <h3>{service.title}</h3>
                    <p>{service.text}</p>
                  </article>
                ))}
              </div>

              <div className="kj-pdp-after-sale__process">
                {afterSaleSteps.map((step) => (
                  <article key={step.number}>
                    <div className="kj-pdp-after-sale__process-media">
                      <img src={step.image} alt={step.alt} />
                    </div>

                    <div className="kj-pdp-after-sale__process-copy">
                      <div className="kj-pdp-after-sale__process-title">
                        <span>{step.number}</span>
                        <h3>{step.title}</h3>
                      </div>

                      <p>{step.text}</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>

          {isSizeGuideOpen && (
            <div
              className="kj-pdp-size-modal"
              role="presentation"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                  setIsSizeGuideOpen(false);
                }
              }}
            >
              <section
                className="kj-pdp-size-modal__dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="kj-size-modal-title"
              >
                <button
                  type="button"
                  className="kj-pdp-size-modal__close"
                  aria-label="Đóng hướng dẫn size"
                  onClick={() => setIsSizeGuideOpen(false)}
                >
                  ×
                </button>

                <div className="kj-pdp-size-modal__eyebrow">
                  Ring Size SVG
                </div>

                <h2 id="kj-size-modal-title">Ướm thử size vòng</h2>

                <p className="kj-pdp-size-modal__intro">
                  Mở bảng SVG để khách tham khảo đường kính trong của vòng.
                  Muốn đo chính xác nên in ở tỉ lệ 100% hoặc dùng thước đo thực tế.
                </p>

                <div className="kj-pdp-size-modal__board">
                  <svg
                    className="kj-pdp-size-modal__svg"
                    viewBox="0 0 900 510"
                    role="img"
                    aria-label="Bảng tham khảo size vòng ngọc từ 50 đến 58 mm"
                  >
                    <defs>
                      <linearGradient
                        id="kj-size-soft-bg"
                        x1="0"
                        y1="0"
                        x2="1"
                        y2="1"
                      >
                        <stop offset="0%" stopColor="#fffdf8" />
                        <stop offset="100%" stopColor="#f8f1df" />
                      </linearGradient>
                    </defs>

                    <rect
                      x="1"
                      y="1"
                      width="898"
                      height="508"
                      rx="34"
                      fill="url(#kj-size-soft-bg)"
                    />

                    <text x="72" y="62" className="kj-size-svg-title">
                      Vạch kiểm tra tham khảo
                    </text>

                    <line
                      x1="72"
                      y1="92"
                      x2="828"
                      y2="92"
                      className="kj-size-svg-ruler"
                    />

                    {[72, 198, 324, 450, 576, 702, 828].map((x) => (
                      <line
                        key={x}
                        x1={x}
                        y1="92"
                        x2={x}
                        y2="116"
                        className="kj-size-svg-ruler"
                      />
                    ))}

                    <text x="72" y="148" className="kj-size-svg-note">
                      Đặt thước thật lên vạch này để kiểm tra tỉ lệ màn hình/in ấn.
                    </text>

                    {[
                      { cx: 164, r: 58, size: "50 mm", label: "SIZE NHỎ" },
                      { cx: 322, r: 61, size: "52 mm", label: "SIZE 52" },
                      { cx: 480, r: 64, size: "54 mm", label: "SIZE 54" },
                      { cx: 638, r: 67, size: "56 mm", label: "SIZE 56" },
                      { cx: 796, r: 70, size: "58 mm", label: "SIZE 58" },
                    ].map((ring) => (
                      <g key={ring.size}>
                        <circle
                          cx={ring.cx}
                          cy="288"
                          r={ring.r}
                          className="kj-size-svg-ring"
                        />
                        <text
                          x={ring.cx}
                          y="296"
                          textAnchor="middle"
                          className="kj-size-svg-size"
                        >
                          {ring.size}
                        </text>
                        <text
                          x={ring.cx}
                          y="370"
                          textAnchor="middle"
                          className="kj-size-svg-label"
                        >
                          {ring.label}
                        </text>
                      </g>
                    ))}

                    <rect
                      x="90"
                      y="418"
                      width="720"
                      height="48"
                      rx="24"
                      className="kj-size-svg-tip-bg"
                    />

                    <text
                      x="450"
                      y="448"
                      textAnchor="middle"
                      className="kj-size-svg-tip"
                    >
                      Gợi ý: đo đường kính trong của vòng vừa tay, rồi so với vòng tròn gần nhất.
                    </text>
                  </svg>
                </div>

                <div className="kj-pdp-size-modal__notice">
                  <strong>Lưu ý đo size</strong>
                  <span>
                    Kích thước trên màn hình có thể lệch tùy thiết bị. Bảng này
                    dùng để tham khảo nhanh; tư vấn viên vẫn cần xác nhận lại
                    size trước khi chốt.
                  </span>
                </div>
              </section>
            </div>
          )}
        </main>
      </div>

      <Footer />
    </>
  );
};

export default ProductDetailPage;
